"use client";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { EnvelopeIcon, PhoneIcon, MapPinIcon, ClockIcon, SparklesIcon } from '@heroicons/react/24/outline';

export default function ContactInfo() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: isMobile ? 10 : 20 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: isMobile ? 0.3 : 0.5, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: isMobile ? 0.95 : 0.8 },
    show: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: isMobile ? 0.3 : 0.6, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const staggerContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: isMobile ? 0.1 : 0.15
      }
    }
  };

  const contactItems = [
    {
      icon: EnvelopeIcon,
      title: "Email",
      value: "contact@connectrh.fr",
      description: "Écrivez-nous pour toute question",
      gradient: "from-violet-500 to-purple-600",
      bgGradient: "from-violet-50 to-purple-50",
      borderColor: "border-violet-200"
    },
    {
      icon: PhoneIcon,
      title: "Téléphone",
      value: "+33 1 23 45 67 89",
      description: "Appelez-nous pour un échange direct",
      gradient: "from-blue-500 to-cyan-600",
      bgGradient: "from-blue-50 to-cyan-50",
      borderColor: "border-blue-200"
    },
    {
      icon: MapPinIcon,
      title: "Adresse",
      value: "Paris, France",
      description: "Siège social",
      gradient: "from-emerald-500 to-teal-600",
      bgGradient: "from-emerald-50 to-teal-50",
      borderColor: "border-emerald-200"
    },
    {
      icon: ClockIcon,
      title: "Horaires",
      value: "9h - 18h",
      description: "Du lundi au vendredi",
      gradient: "from-orange-500 to-red-600",
      bgGradient: "from-orange-50 to-red-50",
      borderColor: "border-orange-200"
    }
  ];

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: isMobile ? 0.05 : 0.1, margin: isMobile ? "-50px" : "-100px" }}
      className="space-y-10"
    >
      {/* En-tête sans badge */}
      <motion.div variants={fadeInUp} className="text-center lg:text-left">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          <span className="bg-gradient-to-r from-violet-600 to-purple-600 gradient-text-safe">
            Contactez-nous
          </span>
        </h2>
        <p className="text-gray-600 leading-relaxed text-lg max-w-2xl">
          Notre équipe d'experts est à votre disposition pour vous accompagner dans la mise en place de ConnectRH. 
          <span className="font-semibold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
            {" "}N'hésitez pas à nous contacter
          </span>
          {" "}pour discuter de vos besoins spécifiques.
        </p>
      </motion.div>

      {/* Informations de contact */}
      <motion.div variants={staggerContainer} className="space-y-6">
        {contactItems.map((item, index) => (
          <motion.div
            key={index}
            variants={scaleIn}
            whileHover={!isMobile ? { 
              y: -5,
              transition: { duration: 0.3 }
            } : {}}
            className={`bg-gradient-to-br ${item.bgGradient} rounded-3xl p-8 border-2 ${item.borderColor} shadow-lg hover:shadow-2xl transition-all duration-300 relative overflow-hidden group`}
          >
            {/* Effet de brillance au survol - désactivé sur mobile */}
            {!isMobile && (
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            )}
            
            <div className="flex items-start space-x-6 relative">
              {!isMobile ? (
                <motion.div
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                  className={`w-16 h-16 bg-gradient-to-r ${item.gradient} rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}
                >
                  <item.icon className="w-8 h-8 text-white" />
                </motion.div>
              ) : (
                <div className={`w-16 h-16 bg-gradient-to-r ${item.gradient} rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                  <item.icon className="w-8 h-8 text-white" />
                </div>
              )}
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className={`bg-gradient-to-r ${item.gradient} bg-clip-text text-transparent font-bold text-lg mb-2`}>
                  {item.value}
                </p>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
} 